import dayjs from 'dayjs'
import {
    FilterBar,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { DEPARTMENT_IDS } from '@/constants/departments'
import { getEntityConfig } from '@/components/Form/Shared/SimpleAsyncSelect/config'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import { Banknote, LayoutGrid, Table, Users } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { exportRevenueCombined, getRevenueCombined } from '../../services'
import StatisticsCards from './StatisticsCards'
import { ViewContent } from './view-content'
import Paper from '@/components/Paper'
import ButtonGroupCustomized from '@/components/ButtonGroupCustomized'

const today = dayjs()

// Map "true"/"false"/other string to boolean | undefined for tri-state select filters
const parseTriStateBoolean = (val: string): boolean | undefined => {
    if (val === 'true') return true
    if (val === 'false') return false
    return undefined
}

type ViewMode = 'overview' | 'detail' | 'by_customer' | 'by_payment_date'

interface AllocationsFilters {
    gte?: string // YYYY-MM-DD
    lte?: string // YYYY-MM-DD
    view?: string // ViewMode
    pdv?: string // payment date sub-view
    product_group_names?: string[]
    sale_manager_codes?: number[]
    account_manager_codes?: number[]
    is_actual_revenue?: boolean
    is_paid?: boolean
    is_allocated?: boolean
}

const RevenueCombinedList: React.FC = () => {
    const { t } = useTranslation('report')
    const [isExporting, setIsExporting] = useState(false)

    const {
        params,
        filters,
        queryParams,
        setFilter,
        setFilters,
        setParams,
        searchInputProps,
    } = useUrlSearchParams<AllocationsFilters>({
        defaults: {
            page_size: 50,
            sort_by: 'allocated_amount',
            sort_order: 'desc',
        },
        filterDefaults: {
            gte: today.startOf('quarter').format('YYYY-MM-DD'),
            lte: today.endOf('quarter').format('YYYY-MM-DD'),
            view: 'overview',
            pdv: 'overview',
        },
    })

    const { control } = useForm<AllocationsFilters>({
        defaultValues: filters,
    })

    const viewMode = (filters.view || 'overview') as ViewMode
    const paymentDateSubView = (filters.pdv || 'overview') as
        | 'overview'
        | 'detail'
    const debouncedSearch = queryParams.q

    // Filter items config for FilterBar
    const employeeConfig = getEntityConfig('employee')
    const filterItems: FilterItemConfig[] = [
        {
            name: 'product_group_names',
            type: 'async-select',
            label: t('product_group_name'),
            placeholder: t('product_group_name.placeholder'),
            asyncOptions: getProductsGroupOptions,
            multiple: true,
        },
        {
            name: 'sale_manager_codes',
            type: 'async-select',
            label: t('sale_manager_codes'),
            placeholder: t('sale_manager_codes.placeholder'),
            asyncOptions: (query, page) =>
                employeeConfig.fetchOptionsWithPage(query, page || 1, {
                    departmentIds: [
                        DEPARTMENT_IDS.SALES,
                        DEPARTMENT_IDS.ACCOUNT,
                        DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
                    ],
                    activeOnly: true,
                }),
            getDefaultOptions: (ids) => employeeConfig.fetchByIds(ids),
            multiple: true,
        },
        {
            name: 'account_manager_codes',
            type: 'async-select',
            label: t('account_manager_codes'),
            placeholder: t('account_manager_codes.placeholder'),
            asyncOptions: (query, page) =>
                employeeConfig.fetchOptionsWithPage(query, page || 1, {
                    departmentIds: [DEPARTMENT_IDS.ACCOUNT],
                    activeOnly: true,
                }),
            getDefaultOptions: (ids) => employeeConfig.fetchByIds(ids),
            multiple: true,
        },
        {
            name: 'is_actual_revenue',
            type: 'checkbox',
            label: t('only_actual_revenue'),
            bordered: true,
            className: 'truncate',
        },
        {
            name: 'is_paid',
            type: 'select',
            label: t('payment_status'),
            placeholder: t('payment_status_placeholder'),
            options: [
                { label: t('paid'), value: 'true' },
                { label: t('unpaid'), value: 'false' },
            ],
            onChange: (val: string) =>
                setFilter('is_paid', parseTriStateBoolean(val)),
        },
        {
            name: 'is_allocated',
            type: 'select',
            label: t('allocations.allocated'),
            placeholder: t('allocations.allocated'),
            options: [
                { label: t('allocated'), value: 'true' },
                { label: t('not_allocated'), value: 'false' },
            ],
            onChange: (val: string) =>
                setFilter('is_allocated', parseTriStateBoolean(val)),
        },
        {
            label: t('date_range'),
            name: 'date_range',
            type: 'daterangepicker',
            names: ['gte', 'lte'],
            showPresets: true,
        },
    ]

    // API filters (exclude UI-only fields, map is_actual_revenue → is_projected)
    const apiFilters = useMemo(
        () => ({
            gte: filters.gte ? dayjs(filters.gte).format('YYYY-MM') : undefined,
            lte: filters.lte ? dayjs(filters.lte).format('YYYY-MM') : undefined,
            product_group_names: filters.product_group_names,
            sale_manager_codes: filters.sale_manager_codes,
            account_manager_codes: filters.account_manager_codes,
            is_paid: filters.is_paid,
            is_allocated: filters.is_allocated,
            ...(filters.is_actual_revenue ? { is_projected: false } : {}),
        }),
        [filters]
    )

    // Fetch summary data (for statistics cards)
    const { data: summaryResponse } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BI_REVENUE_ALLOCATIONS,
            'summary',
            apiFilters,
            debouncedSearch,
        ],
        queryFn: () =>
            getRevenueCombined({
                ...apiFilters,
                search: debouncedSearch || undefined,
                page: 1,
                page_size: 1,
            }),
        staleTime: 5 * 60 * 1000,
    })

    // Fetch data for detail view (with pagination)
    const {
        data: detailResponse,
        isLoading: isLoadingDetail,
        isFetching: isFetchingDetail,
    } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BI_REVENUE_ALLOCATIONS,
            'detail',
            apiFilters,
            params.page,
            params.page_size,
            debouncedSearch,
        ],
        queryFn: () =>
            getRevenueCombined({
                ...apiFilters,
                page: params.page,
                page_size: params.page_size,
                search: debouncedSearch || undefined,
                sort_by: 'allocated_amount',
                sort_order: 'desc',
            }),
        enabled: viewMode === 'detail',
        staleTime: 5 * 60 * 1000,
    })

    const data = detailResponse?.results || []
    const pagination = detailResponse?.pagination
    const summary = summaryResponse?.summary

    const statistics = useMemo(() => {
        if (!summary) {
            return {
                totalRevenue: 0,
                actualRevenue: 0,
                projectedRevenue: 0,
                paidAmount: 0,
                unpaidAmount: 0,
            }
        }
        return {
            totalRevenue: summary.total_allocated_amount || 0,
            actualRevenue: summary.total_actual_amount || 0,
            projectedRevenue: summary.total_projected_amount || 0,
            paidAmount: summary.total_paid_amount || 0,
            unpaidAmount: summary.total_unpaid_amount || 0,
        }
    }, [summary])

    // Export handler
    const handleExport = async () => {
        try {
            setIsExporting(true)
            await exportRevenueCombined({
                ...apiFilters,
                search: debouncedSearch || undefined,
            })
            toast.success(t('export_success'))
        } catch {
            toast.error(t('export_error'))
        } finally {
            setIsExporting(false)
        }
    }

    return (
        <div className="space-y-2">
            {/* Filters */}
            <Paper>
                <FilterBar
                    control={control}
                    setFilter={setFilter}
                    setFilters={setFilters}
                    items={filterItems}
                    hideSearch
                />
            </Paper>

            {/* Statistics Cards */}
            <StatisticsCards statistics={statistics} summary={summary} />

            <Paper className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <ButtonGroupCustomized
                        className="md:max-w-[600px]"
                        buttons={[
                            {
                                icon: <LayoutGrid className="h-4 w-4 mr-1" />,
                                label: t('overview.label'),
                                value: 'overview',
                            },
                            {
                                icon: <Table className="h-4 w-4 mr-1" />,
                                label: t('detail'),
                                value: 'detail',
                            },
                            {
                                icon: <Users className="h-4 w-4 mr-1" />,
                                label: t('by_customer'),
                                value: 'by_customer',
                            },
                            {
                                icon: <Banknote className="h-4 w-4 mr-1" />,
                                label: t('by_payment_date'),
                                value: 'by_payment_date',
                            },
                        ]}
                        active={viewMode}
                        onChange={(value) =>
                            setFilter(
                                'view',
                                value as
                                    | 'overview'
                                    | 'detail'
                                    | 'by_customer'
                                    | 'by_payment_date'
                            )
                        }
                    />
                </div>

                <ViewContent
                    viewMode={viewMode}
                    paymentDateSubView={paymentDateSubView}
                    filters={filters}
                    apiFilters={apiFilters}
                    debouncedSearch={debouncedSearch}
                    data={data}
                    isLoadingDetail={isLoadingDetail}
                    isFetchingDetail={isFetchingDetail}
                    params={params}
                    pagination={pagination}
                    searchInputProps={searchInputProps}
                    setParams={setParams}
                    setFilter={setFilter}
                    isExporting={isExporting}
                    handleExport={handleExport}
                />
            </Paper>
        </div>
    )
}

export default RevenueCombinedList
